import { useCallback, useEffect, useMemo } from 'react';
import * as tracker from 'analytics/tracker';

import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { QuoteProduct, QuoteProductStatus, QuoteRequestStatus, deleteQuoteProduct } from 'app/api/quotes';
import { useAuth, useCancelQuoteRequestMutation } from 'hooks';
import { Currency, currencyFormat } from 'utils/currencyFormat';
import { AnalyticsName } from 'analytics';
import { QuoteTableFormData } from '../types';

interface UseQuoteDiscussionRowProps {
  data: QuoteProduct;
  control: Control<QuoteTableFormData>;
  index: number;
  quoteId: number;
  isNotlastProduct: boolean;
  isSampleRequest: boolean;
  priceUnits: Currency;
  removeChoosedProducts: (index: number) => void;
  setValue: UseFormSetValue<QuoteTableFormData>;
  increaseConfirmedProducsCount: () => void;
  decreaseConfirmedProducsCount: () => void;
  handleCloseModal: () => void;
  closeDetails: () => void;
  handleCloseEditAmountModal: () => void;
  handleNavigate?: () => void;
}

export const useQuoteDiscussionRow = ({
  control,
  data,
  index,
  isNotlastProduct,
  isSampleRequest,
  quoteId,
  closeDetails,
  decreaseConfirmedProducsCount,
  handleCloseEditAmountModal,
  handleCloseModal,
  increaseConfirmedProducsCount,
  removeChoosedProducts,
  setValue,
  priceUnits,
  handleNavigate,
}: UseQuoteDiscussionRowProps) => {
  const queryClient = useQueryClient();
  const { isBuyer } = useAuth();

  const { mutate: removeProduct, isLoading } = useMutation((id: number) => deleteQuoteProduct(id), {
    onSuccess: () => {
      if (isNotlastProduct) {
        handleRemove();
      }
      try {
        const analyticsOptions = {
          quote_id: quoteId,
          product_id: data.product.id,
          quote_product_id: data.id,
          crop_id: data.product.crop,
        };
        tracker.track(AnalyticsName.QUOTE_PRODUCT_DELETE, analyticsOptions);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('rudderanalytics.track error:', e);
      }

      handleCloseEditAmountModal();
    },
  });

  const { isLoadingCancel, isCancelSuccess, handleCancelRequest } = useCancelQuoteRequestMutation({
    isSampleRequest,
    quoteId,
  });

  const quotePrice = useWatch({
    control,
    name: `quotes.${index}.value`,
  });

  const [counterOfferPrice, counterOfferCurrency] = useMemo(() => {
    if (data.acceptedPrice) return [data.acceptedPrice, priceUnits];
    if (isBuyer && data.latestSellerPrice?.price)
      return [data.latestSellerPrice.price, data.latestSellerPrice.priceUnits];
    if (!isBuyer && data.latestBuyerPrice?.price)
      return [data.latestBuyerPrice.price, data.latestBuyerPrice.priceUnits];
    return [0, priceUnits];
  }, [data, isBuyer, priceUnits]);

  const totalPrice = useMemo(() => {
    if (data.acceptedPrice) {
      return currencyFormat(data.amount * data.acceptedPrice, priceUnits);
    }
    if (quotePrice) {
      return currencyFormat(data.amount * parseFloat(quotePrice), priceUnits);
    }
    if (isBuyer && data.latestSellerPrice?.price) {
      return currencyFormat(data.amount * data.latestSellerPrice.price, data.latestSellerPrice.priceUnits);
    }
    if (!isBuyer && data.latestBuyerPrice?.price) {
      return currencyFormat(data.amount * data.latestBuyerPrice.price, data.latestBuyerPrice.priceUnits);
    }
    if (data.latestSellerPrice) {
      return currencyFormat(data.amount * data.latestSellerPrice.price, data.latestSellerPrice.priceUnits);
    }
    return '-';
  }, [data, isBuyer, priceUnits, quotePrice]);

  const lastOffer = useMemo(
    () => currencyFormat(Number(counterOfferPrice) || 0, counterOfferCurrency),
    [counterOfferPrice, counterOfferCurrency],
  );

  const isConfirm = useWatch({
    control,
    name: `quotes.${index}.updateAcceptedPrice`,
  });

  const quoteProductStatus = useWatch({
    control,
    name: `quotes.${index}.status`,
  });

  const handleConfirm = useCallback(() => {
    setValue(`quotes.${index}.updateAcceptedPrice`, counterOfferPrice);
    setValue(`quotes.${index}.acceptedPrice`, counterOfferPrice);
    increaseConfirmedProducsCount();
  }, [setValue, index, increaseConfirmedProducsCount, counterOfferPrice]);

  const handleUndo = useCallback(() => {
    setValue(`quotes.${index}.updateAcceptedPrice`, 0);
    setValue(`quotes.${index}.acceptedPrice`, 0);
    decreaseConfirmedProducsCount();
  }, [setValue, index, decreaseConfirmedProducsCount]);

  const handleRemove = useCallback(() => {
    if (!isConfirm && !data.acceptedPrice) {
      increaseConfirmedProducsCount();
    }

    removeChoosedProducts(index);
    handleCloseModal();

    queryClient.removeQueries('quote-messages');
  }, [
    isConfirm,
    handleCloseModal,
    removeChoosedProducts,
    index,
    increaseConfirmedProducsCount,
    queryClient,
    data.acceptedPrice,
  ]);

  const handleRemoveRequest = useCallback(() => {
    removeProduct(data.id);
  }, [data, removeProduct]);

  const handleCancel = useCallback(() => {
    handleRemoveRequest();
    handleCancelRequest({ status: QuoteRequestStatus.CANCELED });
    handleNavigate?.();
  }, [handleRemoveRequest, handleCancelRequest, handleNavigate]);

  const isCancelLoading = isLoadingCancel || isLoading;

  const isActive = quoteProductStatus === QuoteProductStatus.AVAILABLE;
  const isRemoved = quoteProductStatus === QuoteProductStatus.REMOVED;
  const isUnavailable = quoteProductStatus === QuoteProductStatus.NOT_AVAILABLE;
  const isRestricted = quoteProductStatus === QuoteProductStatus.RESTRICTED;
  const isOpacity = !isActive;
  const isSellerInputNeeded = isBuyer && isActive && !lastOffer;

  useEffect(() => {
    if (isCancelSuccess) {
      closeDetails();
    }
  }, [isCancelSuccess, closeDetails]);

  return {
    quotePrice,
    isLoading,
    lastOffer,
    totalPrice,
    isConfirm,
    isOpacity,
    isActive,
    isRemoved,
    isUnavailable,
    isRestricted,
    isSellerInputNeeded,
    counterOfferPrice,
    isCancelLoading,
    handleConfirm,
    handleUndo,
    handleRemoveRequest,
    handleCancel,
  };
};
